import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

// EST timezone
const EST_TIMEZONE = 'America/New_York';

/**
 * Formats a UTC date string to EST in the format: "Wed, Oct 15, 2025 - 8:30pm"
 * @param {string} utcDateString - UTC date string (e.g., "2025-09-19T00:30:00Z")
 * @returns {string} Formatted date string
 */
export const formatGameDateTime = (utcDateString) => {
  try {
    // Convert UTC to EST
    const utcDate = new Date(utcDateString);
    const estDate = toZonedTime(utcDate, EST_TIMEZONE);
    
    // Format as requested: "Wed, Oct 15, 2025 - 8:30pm"
    const datePart = format(estDate, 'EEE, MMM d, yyyy');
    const timePart = format(estDate, 'h:mm aaa').toLowerCase();
    
    return `${datePart} - ${timePart}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Formats a UTC date string to EST date only: "Wed, Oct 15, 2025"
 * @param {string} utcDateString - UTC date string
 * @returns {string} Formatted date string
 */
export const formatGameDate = (utcDateString) => {
  try {
    const utcDate = new Date(utcDateString);
    const estDate = toZonedTime(utcDate, EST_TIMEZONE);
    return format(estDate, 'EEE, MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Formats a UTC date string to EST time only: "8:30pm"
 * @param {string} utcDateString - UTC date string
 * @returns {string} Formatted time string
 */
export const formatGameTime = (utcDateString) => {
  try {
    const utcDate = new Date(utcDateString);
    const estDate = toZonedTime(utcDate, EST_TIMEZONE);
    return format(estDate, 'h:mm aaa').toLowerCase();
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

/**
 * Returns the calendar date ("yyyy-MM-dd") that formatGameDate displays for a game start,
 * so grouping by day always matches the shown date. Returns null for invalid input.
 * @param {string} utcDateString - UTC date string
 * @returns {string|null} Date key
 */
export const getGameDateKey = (utcDateString) => {
  if (!utcDateString) return null;
  const utcDate = new Date(utcDateString);
  if (Number.isNaN(utcDate.getTime())) return null;
  return format(toZonedTime(utcDate, EST_TIMEZONE), 'yyyy-MM-dd');
};

const parseDateKey = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a range of date keys ("yyyy-MM-dd"):
 * "Thu, Oct 30, 2025", "Thu, Nov 6 – Sat, Nov 8, 2025" or "Mon, Dec 29, 2025 – Sat, Jan 3, 2026".
 * @param {string} startKey - First date key
 * @param {string} [endKey] - Last date key (defaults to startKey)
 * @returns {string} Formatted date range
 */
export const formatGameDateRange = (startKey, endKey = startKey) => {
  const start = parseDateKey(startKey);
  if (!endKey || endKey === startKey) return format(start, 'EEE, MMM d, yyyy');
  const end = parseDateKey(endKey);
  const startFormat = start.getFullYear() === end.getFullYear() ? 'EEE, MMM d' : 'EEE, MMM d, yyyy';
  return `${format(start, startFormat)} – ${format(end, 'EEE, MMM d, yyyy')}`;
};

// Test the function with your example data
// Expected output: "Thu, Sep 19, 2025 - 8:30pm" (assuming EST)